'use client'
import React, {
  useRef,
  useImperativeHandle,
  forwardRef,
  ReactNode,
} from 'react'
import { XMarkIcon } from '@heroicons/react/16/solid'
import { cn } from 'tailwind-ui'
import { JosefIconButton } from '../josef/buttons'
import { Spinner } from '../spinner'

export interface NativeDialogHandle {
  showModal: () => void
  close: () => void
}

interface NativeDialogProps {
  title: ReactNode
  children: ReactNode
  onClose?: () => void
  avatar?: ReactNode
  footerButton?: {
    text: string
    onClick: () => void
  }
  footerButtons?: {
    close?: {
      text: string
      onClick: () => void
      disabled?: boolean
    }
    continue?: {
      text: string
      onClick: () => void
      disabled?: boolean
      isLoading?: boolean
      variant?: 'default' | 'danger' | 'neutral'
    }
  }
  footerContent?: ReactNode
  use_brand?: boolean
  className?: string
  bodyClassName?: string
  headerClassName?: string
}

export const NativeDialog = forwardRef<NativeDialogHandle, NativeDialogProps>(
  (
    {
      title,
      children,
      onClose,
      avatar,
      footerButton,
      footerButtons,
      footerContent,
      use_brand = false,
      className = '',
      bodyClassName = '',
      headerClassName = '',
    },
    ref
  ) => {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const isClosingRef = useRef(false)

    const handleClose = () => {
      if (isClosingRef.current) return
      isClosingRef.current = true
      dialogRef.current?.close()
      document.body.style.overflow = ''
      onClose?.()
      isClosingRef.current = false
    }

    useImperativeHandle(ref, () => ({
      showModal: () => {
        dialogRef.current?.showModal()
        // Prevent background scrolling
        document.body.style.overflow = 'hidden'
      },
      close: handleClose,
    }))

    return (
      <dialog
        ref={dialogRef}
        className={cn(
          'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 m-0 p-0 backdrop:bg-black/50 backdrop:backdrop-blur-sm backdrop:transition-opacity shadow-2xl overflow-hidden w-full max-h-[90vh]',
          use_brand ? 'rounded-brand' : 'rounded-xl',
          'dark:bg-[#121212] bg-white',
          'max-w-sm sm:max-w-md lg:max-w-lg',
          className
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'px-4 py-3',
            headerClassName || 'bg-neutral-50 dark:bg-[#1a1a1a]'
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {avatar}
              <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                {title}
              </div>
            </div>
            <JosefIconButton
              variant="ghost"
              size="icon-sm"
              icon={<XMarkIcon className="w-4 h-4" />}
              onClick={handleClose}
              aria-label="Close"
            />
          </div>
        </div>

        {/* Content */}
        <div
          className={cn(
            'text-neutral-900 dark:text-neutral-100',
            bodyClassName ||
              'px-4 pt-4 pb-6 max-h-96 sm:max-h-[28rem] lg:max-h-[32rem] overflow-y-auto relative'
          )}
        >
          {children}
        </div>

        {/* Footer */}
        {(footerButton || footerButtons || footerContent) && (
          <div className="sticky bottom-0 bg-white dark:bg-[#121212]">
            {footerContent ? (
              <div className="px-4 py-4 border-t border-neutral-200 dark:border-[#232323]">
                {footerContent}
              </div>
            ) : footerButton ? (
              <button
                type="button"
                onClick={footerButton.onClick}
                className={cn(
                  "w-full py-6 text-sm font-medium focus:ring-2 focus:ring-offset-2 transition-colors font-body",
                  use_brand
                    ? "bg-brand text-brand-invert hover:bg-interface-700 focus:ring-brand"
                    : "bg-black text-white dark:bg-white dark:text-black hover:bg-neutral-800 dark:hover:bg-neutral-200 focus:ring-black dark:focus:ring-white"
                )}
              >
                {footerButton.text}
              </button>
            ) : footerButtons ? (
              <div className="flex">
                {footerButtons.close && (
                  <button
                    type="button"
                    onClick={footerButtons.close.onClick}
                    disabled={footerButtons.close.disabled}
                    className="flex-1 py-6 text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors font-body disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {footerButtons.close.text}
                  </button>
                )}
                {footerButtons.continue && (
                  <button
                    type="button"
                    onClick={footerButtons.continue.onClick}
                    disabled={footerButtons.continue.disabled || footerButtons.continue.isLoading}
                    className={`flex-1 py-6 text-sm font-medium transition-colors font-body disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                      footerButtons.continue.variant === 'danger'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : footerButtons.continue.variant === 'neutral'
                          ? 'bg-black text-white dark:bg-white dark:text-black hover:bg-neutral-800 dark:hover:bg-neutral-200'
                          : use_brand
                            ? 'bg-brand text-brand-invert hover:bg-interface-700'
                            : 'bg-black text-white dark:bg-white dark:text-black hover:bg-neutral-800 dark:hover:bg-neutral-200'
                    }`}
                  >
                    {footerButtons.continue.isLoading ? (
                      <Spinner size="sm" />
                    ) : null}
                    {footerButtons.continue.text}
                  </button>
                )}
              </div>
            ) : null}
          </div>
        )}
      </dialog>
    )
  }
)

NativeDialog.displayName = 'NativeDialog'
